import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  alpha,
  CircularProgress,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import axiosInstance from '../../../utils/axios';
import { canManageAllClaims } from '../../../utils/financialPermissions';

const NEON = '#38BDF8';
const NEON3 = '#34D399';
const SURFACE_UP = '#0f1f38';
const TEXT_BODY = '#E2E8F0';
const TEXT_MUTED = '#64748B';

/**
 * Stripe Checkout demo (test mode) for reimbursing an approved claim.
 */
export default function ClaimStripePay({ claim, currentUser, onPaid }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const canPay =
    canManageAllClaims(currentUser) &&
    claim &&
    claim.status === 'approved' &&
    !claim.is_paid &&
    parseFloat(claim.amount || 0) > 0;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/claims/stripe/config');
        if (!cancelled) setConfig(res.data);
      } catch {
        if (!cancelled) setConfig({ enabled: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!canPay || !config?.enabled) {
    return null;
  }

  const startCheckout = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.post(`/claims/${claim.id}/stripe/checkout`);
      const url = res.data?.checkout_url;
      if (!url) throw new Error('No checkout URL returned');
      window.location.href = url;
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        'Could not start Stripe demo checkout';
      setError(msg);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: '12px',
        border: `1px solid ${alpha(NEON, 0.22)}`,
        background: alpha(SURFACE_UP, 0.55),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <CreditCardIcon sx={{ fontSize: 18, color: NEON }} />
        <Typography
          sx={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 700,
            fontSize: '0.78rem',
            color: TEXT_BODY,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Stripe demo reimbursement
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 1.5, fontSize: '0.8rem' }}>
        Sandbox only (test keys). Use card{' '}
        <strong>{config.test_cards_hint || '4242 4242 4242 4242'}</strong> — no real
        charges. After payment, this claim is marked paid automatically.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        disabled={loading}
        onClick={startCheckout}
        startIcon={
          loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <CreditCardIcon />
          )
        }
        sx={{
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          background: `linear-gradient(135deg, ${NEON} 0%, ${NEON3} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${alpha(NEON, 0.9)} 0%, ${alpha(NEON3, 0.9)} 100%)`,
          },
        }}
      >
        {loading ? 'Redirecting to Stripe…' : `Pay RM ${parseFloat(claim.amount || 0).toFixed(2)} with Stripe (demo)`}
      </Button>

      <Typography sx={{ mt: 1, fontSize: '0.72rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
        Powered by Stripe Checkout · {config.currency || 'MYR'} · test mode
      </Typography>
    </Box>
  );
}
